// The walk (docs/job-view-brief.md, section 2): the order Spencer checks a job in, what each item changed from
// standard, what the job check found on it, whether its Checked mark stands, and the Fronts view drawn from the
// resolved door sizes and the reveals the boxes read. A view of the resolution and the job's rows; nothing here
// prices or stores anything.

import type { Library, Resolution, ResolvedBom, ResolvedItem, ResolvedStep, Scalar } from './types.ts';
import type { Finding } from './check.ts';
import { formatLength } from './units.ts';

// ---------- the order ----------

export interface WalkView {
  id: string;
  name: string | null;
  sort: number;
}
export interface WalkMark {
  view_id: string;
  job_item_id: string;
  x_pct: number | string;
  y_pct: number | string;
  /** A region mark (2026-10-24): its width and height in percent of the sheet, around the point; null or absent on a point. */
  w_pct?: number | string | null;
  h_pct?: number | string | null;
}

/** One stop on the walk: a top-level item (its children are walked with it), the job route, or the Job BOM. */
export interface WalkStop {
  kind: 'item' | 'job_route' | 'job_bom';
  key: string;
  item: ResolvedItem | null;
  /** The View the item is marked on; null in Whole room. */
  view_id: string | null;
  /** The heading the tree groups the stop under: the catalog item's name, "Custom", or "Whole room". */
  group: string;
}
export interface WalkWall {
  view: WalkView | null;
  name: string;
  stops: WalkStop[];
}

const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/**
 * The job walked one View at a time in View order, within a View by kind of item (its catalog item, custom items last)
 * then by number, and Whole room last: every top-level item with no mark on any current View, then the job route and
 * the Job BOM. The list never re-sorts while a job is open: it reads from the rows, not from what was checked.
 */
export function walkOrder(res: Resolution, views: WalkView[], marks: WalkMark[], lib: Library): WalkWall[] {
  const top = res.items.filter((i) => !i.parent_item_id);
  const kindOf = (i: ResolvedItem) => (i.is_custom ? '￿ Custom' : `${lib.catalog[i.catalog_item_id ?? '']?.family ?? ''} ${i.catalog_item_name ?? i.name}`);
  const groupOf = (i: ResolvedItem) => (i.is_custom ? 'Custom' : i.catalog_item_name ?? i.name);
  const byKind = (a: ResolvedItem, b: ResolvedItem) => natural(kindOf(a), kindOf(b)) || natural(a.number, b.number) || a.sort - b.sort;
  const placed = new Set<string>();
  const walls: WalkWall[] = [];
  for (const v of [...views].sort((a, b) => a.sort - b.sort)) {
    const here = new Set(marks.filter((m) => m.view_id === v.id).map((m) => m.job_item_id));
    const items = top.filter((i) => here.has(i.id) && !placed.has(i.id)).sort(byKind);
    for (const i of items) placed.add(i.id);
    walls.push({ view: v, name: v.name ?? 'View', stops: items.map((i) => ({ kind: 'item', key: i.id, item: i, view_id: v.id, group: groupOf(i) })) });
  }
  const rest = top.filter((i) => !placed.has(i.id)).sort(byKind);
  const room: WalkStop[] = rest.map((i) => ({ kind: 'item', key: i.id, item: i, view_id: null, group: 'Whole room' }));
  const job = res.groups.find((g) => g.kind === 'job');
  if (job && job.steps.length > 0) room.push({ kind: 'job_route', key: 'job_route', item: null, view_id: null, group: 'Whole room' });
  if (job && job.bom.length > 0) room.push({ kind: 'job_bom', key: 'job_bom', item: null, view_id: null, group: 'Whole room' });
  walls.push({ view: null, name: 'Whole room', stops: room });
  return walls;
}

/** Every stop in walk order, flat. */
export function walkStops(walls: WalkWall[]): WalkStop[] {
  return walls.flatMap((w) => w.stops);
}

// ---------- what is placed, and what is not ----------

export interface Placement {
  /** The top-level items that want a mark: a catalog item whose mark threshold is not no_mark, or a custom item; an alternate is counted with the item it stands in for. */
  wanted: ResolvedItem[];
  /** Those with no mark on any current View ("Not placed yet"), by number. */
  unplaced: ResolvedItem[];
  /** How many of the wanted items are marked on each View, by View id. */
  on_view: Record<string, number>;
}

/**
 * The "Not placed yet" group and the "12 of 23" count (Spencer, 2026-10-23, the Views pack): every top-level item that wants a mark and
 * has one on no current View. A Whole-room item by design (mark threshold no_mark: a vendor order, the job-wide sub) is not wanted and
 * never listed. Alternates share a number and a mark (V8): an item standing in for another has no mark of its own and sits where the
 * item it replaces sits, so it is neither counted nor listed; its base is. The count is by number, not by item.
 */
export function placement(res: Resolution, views: WalkView[], marks: WalkMark[]): Placement {
  const current = new Set(views.map((v) => v.id));
  const wanted = res.items.filter((i) => !i.parent_item_id && !i.replaces_item_id && i.mark_threshold !== 'no_mark').sort((a, b) => natural(a.number, b.number) || a.sort - b.sort);
  const marked = new Set(marks.filter((m) => current.has(m.view_id)).map((m) => m.job_item_id));
  const unplaced = wanted.filter((i) => !marked.has(i.id));
  const on_view: Record<string, number> = {};
  for (const v of views) {
    const here = new Set(marks.filter((m) => m.view_id === v.id).map((m) => m.job_item_id));
    on_view[v.id] = wanted.filter((i) => here.has(i.id)).length;
  }
  return { wanted, unplaced, on_view };
}

// ---------- the Checked mark ----------

export interface CheckRow {
  checked_at?: string | null;
  checked_by?: string | null;
  check_version_id?: string | null;
  check_cleared_at?: string | null;
  approved?: Record<string, ApprovedChange> | null;
  answered?: Record<string, AnsweredFinding> | null;
}
export interface ApprovedChange {
  value: unknown;
  at: string;
  by: string | null;
}
export interface AnsweredFinding {
  answer: 'fix' | 'keep';
  at: string;
  by: string | null;
}

/** checked: the mark stands. cleared: an edit cleared a mark the item held, so a recheck lists it. never: not checked yet. */
export function checkState(row: CheckRow | null | undefined): 'checked' | 'cleared' | 'never' {
  if (!row) return 'never';
  if (row.checked_at) return 'checked';
  return row.check_cleared_at ? 'cleared' : 'never';
}

// ---------- changes from standard ----------

export interface Deviation {
  /** Stable across resolutions, so an approval can be kept against it: std:<key>, step:<step id or key>, line:<line id or key>, price:<line id or key>. */
  key: string;
  kind: 'standard' | 'route' | 'bom' | 'pricing';
  /** What is held or changed, in words. */
  label: string;
  /** The value held on this item, in words, and the standard it differs from. */
  value: string;
  standard: string;
  /** The value as stored, compared against an approval's value: an approval holds for as long as the value stays. */
  raw: Scalar;
  /** Approved on the walk and still holding (the value has not moved since). */
  approved: ApprovedChange | null;
}

const words = (lib: Library, type: string, value: Scalar, unit: string | null): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'material') return lib.materials[String(value)]?.description ?? String(value);
  if (type === 'catalog_item') return lib.catalog[String(value)]?.name ?? String(value);
  if (type === 'boolean') return value === true || value === 'true' ? 'yes' : 'no';
  if (type === 'number' && (unit === 'in' || unit === null) && Number.isFinite(Number(value))) return `${formatLength(String(value))}"`;
  if (type === 'number' && unit) return `${value} ${unit}`;
  return String(value);
};

const trim = (s: string) => (s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s);

/** The approval kept for this key, if its value still matches what the item holds now. */
function stillApproved(row: CheckRow | null | undefined, key: string, raw: Scalar): ApprovedChange | null {
  const a = row?.approved?.[key];
  if (!a) return null;
  return String(a.value ?? '') === String(raw ?? '') ? a : null;
}

/**
 * Every change from standard on one item (the existing flags, no second mechanism): a job standard held on the item
 * against the job's value; a recipe route step or BOM line changed or added on this item only; a line's pricing set on
 * this item. Each with the standard beside it, and the approval kept on the row where the value has not moved since.
 */
export function itemDeviations(item: ResolvedItem, res: Resolution, lib: Library, row: CheckRow | null | undefined): Deviation[] {
  const out: Deviation[] = [];
  for (const st of item.standards_used) {
    if (!st.overridden) continue;
    const def = lib.standards.find((d) => d.key === st.key);
    const type = def?.data_type ?? 'text';
    const unit = type === 'number' ? def?.unit ?? null : null;
    const key = `std:${st.key}`;
    out.push({ key, kind: 'standard', label: def?.label ?? st.key, value: words(lib, type, st.value, unit), standard: `standard ${words(lib, type, st.job_value, unit)}`, raw: st.value, approved: stillApproved(row, key, st.value) });
  }
  const group = res.groups.find((g) => g.key === item.group_key);
  if (group) {
    const cat = item.catalog_item_id ? lib.catalog[item.catalog_item_id] : null;
    const recipe = cat?.recipes.find((r) => r.id === item.recipe_id);
    for (const s of group.steps as ResolvedStep[]) {
      if (!s.own || !(s.shares[item.id] && (Number(s.shares[item.id]!.qty ?? 0) > 0 || Number(s.shares[item.id]!.minutes ?? 0) > 0))) continue;
      const key = `step:${s.step_id ?? s.key}`;
      const was = s.own === 'changed' ? recipe?.route.find((r) => r.id === s.step_id)?.qty_expr : null;
      const raw = trim(s.shares[item.id]?.qty ?? s.qty);
      out.push({
        key, kind: 'route', label: `Route · ${s.operation_name}`, value: `${raw} ${s.unit ?? ''}`.trim(),
        standard: s.own === 'added' ? 'not on the catalog item' : `catalog ${was ?? 'formula'}`, raw, approved: stillApproved(row, key, raw),
      });
    }
    for (const b of group.bom as ResolvedBom[]) {
      const mine = b.shares[item.id] && (Number(b.shares[item.id]!.qty ?? 0) > 0 || (b.shares[item.id]!.cents ?? 0) > 0);
      if (b.own && mine) {
        const key = `line:${b.line_id ?? b.key}`;
        const was = b.own === 'changed' ? recipe?.bom.find((r) => r.id === b.line_id) : null;
        const raw = trim(b.shares[item.id]?.qty ?? b.qty);
        out.push({
          key, kind: 'bom', label: `BOM · ${b.description}`, value: `${raw} ${b.unit ?? ''}`.trim(),
          standard: b.own === 'added' ? 'not on the catalog item' : `catalog ${was?.qty_expr ?? 'formula'}${was && was.material_id !== b.material_id ? ` · ${lib.materials[was.material_id ?? '']?.description ?? 'its material'}` : ''}`,
          raw, approved: stillApproved(row, key, raw),
        });
      }
      if (b.pricing_own && mine) {
        const key = `price:${b.line_id ?? b.key}`;
        const raw = `${b.separate_pricing ? 'separate' : 'standard'}|${trim(b.markup_pct)}|${b.outside_margin ? 'outside' : 'in'}`;
        const value = b.separate_pricing ? `Separate pricing · ${trim(b.markup_pct)} %${b.outside_margin ? ' · outside the margin' : ''}` : 'standard pricing';
        out.push({ key, kind: 'pricing', label: `Pricing · ${b.description}`, value, standard: `company markup ${trim(String(lib.pricing.materials_markup_pct))} %, inside the margin`, raw, approved: stillApproved(row, key, raw) });
      }
    }
  }
  return out;
}

// ---------- the check's findings on one item ----------

export interface ItemFinding {
  key: string;
  finding: Finding;
  answered: AnsweredFinding | null;
  /**
   * The flag that reaches the engineer (the Quick check pack, Spencer 2026-10-23): the one "Needs a fix" raised on this
   * finding, or the flag itself when the finding is one a person wrote (rule `flag`). Null when none, or once a check cleared it.
   */
  flag: FlagRow | null;
}

/**
 * One row of ug.job_flag: a person's word to the engineer. Its target is an item, a wall (a View) or the room; a flag raised
 * by "Needs a fix" on a finding carries that finding's key, one written by hand carries none. Open until the engineer closes
 * it with a note; the item's next check clears it from view (cleared_at); nothing is deleted.
 */
export interface FlagRow {
  id: string;
  job_id?: string;
  target: 'item' | 'wall' | 'room';
  job_item_id?: string | null;
  view_id?: string | null;
  finding_key?: string | null;
  text: string;
  note?: string | null;
  seat_id?: string | null;
  created_by?: string | null;
  created_at?: string;
  resolved_by?: string | null;
  resolved_at?: string | null;
  resolution_note?: string | null;
  cleared_at?: string | null;
  cleared_by?: string | null;
}

/** A flag the item still shows: open, or closed and not yet cleared by a check. */
export function flagShowing(f: FlagRow): boolean {
  return !f.cleared_at;
}

/**
 * The job check's findings on one item (by its number, or spread across items it is among), keyed so an answer can be kept;
 * with the job's flags, each finding carries the flag "Needs a fix" raised on it, and a flag written by hand on the item is a
 * finding of its own (rule `flag`): open, it counts in "to fix" and the check waits on it; closed by the engineer it reads as
 * answered, and shows "fixed" until the check clears it (Spencer, 2026-10-23).
 */
export function itemFindings(item: ResolvedItem, findings: Finding[], row: CheckRow | null | undefined, flags: FlagRow[] = []): ItemFinding[] {
  const out: ItemFinding[] = [];
  const mine = flags.filter((f) => f.job_item_id === item.id && flagShowing(f));
  for (const f of findings) {
    if (f.item !== item.number && !(f.items ?? []).includes(item.number)) continue;
    const key = `find:${f.rule}:${hash(f.what)}`;
    out.push({ key, finding: f, answered: row?.answered?.[key] ?? null, flag: mine.find((x) => x.finding_key === key) ?? null });
  }
  for (const f of mine) {
    if (f.finding_key) continue;
    out.push({
      key: `flag:${f.id}`,
      finding: { pass: 2, rule: 'flag', what: f.text, item: item.number },
      answered: f.resolved_at ? { answer: 'fix', at: f.resolved_at, by: f.resolved_by ?? null } : null,
      flag: f,
    });
  }
  return out;
}

/** Findings on no item at all: the job's own (the market check, a sub priced against nothing, a scope row with no item). */
export function jobFindings(findings: Finding[]): Finding[] {
  return findings.filter((f) => !f.item && !(f.items && f.items.length));
}

function hash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** What the check button approves, in words: "Check L-02 and its 3 parts · approves 2 changes". */
export function checkWords(item: ResolvedItem, parts: number, deviations: Deviation[], findings: ItemFinding[]): { label: string; blocked: number } {
  const pending = deviations.filter((d) => !d.approved).length;
  const open = findings.filter((f) => !f.answered).length;
  const partsWords = parts > 0 ? ` and its ${parts} ${parts === 1 ? 'part' : 'parts'}` : '';
  const label = `Check ${item.number}${partsWords}${pending ? ` · approves ${pending} ${pending === 1 ? 'change' : 'changes'}` : ''}`;
  return { label, blocked: open };
}

// ---------- the Fronts view ----------

export interface Front {
  item: ResolvedItem;
  kind: 'door' | 'drawer' | 'panel';
  /** Inches from the box's left face and from the box's bottom face. */
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface Reveal {
  key: string;
  label: string;
  value: number | null;
  standard: number | null;
  held: boolean;
}
export interface FrontsBox {
  item: ResolvedItem;
  /** Inches from the wall's left end: the boxes sit side by side in the order of their marks. */
  x: number;
  width: number;
  height: number;
  fronts: Front[];
  reveals: Record<'left' | 'right' | 'top' | 'bottom' | 'between', Reveal>;
}
export interface Gap {
  key: string;
  /** Inches from the wall's left end, and the gap's width. */
  x: number;
  width: number;
  /** "left 1/8", "between 1/8", "doors 1/8 apart · 1/16 + 1/16" */
  label: string;
  /** The standard the gap reads, in inches, when it differs from the value: held. */
  held: boolean;
  standard: number | null;
  /** The items whose reveals make the gap: one, or two at a joint. */
  item_ids: string[];
  /** The deviation keys one Approve covers: std:reveal_right on one box, std:reveal_left on the next. */
  keys: { item_id: string; key: string; value: Scalar }[];
  row: 0 | 1;
}
export interface FrontsLayout {
  boxes: FrontsBox[];
  gaps: Gap[];
  width: number;
  height: number;
}

const REVEALS: { k: 'left' | 'right' | 'top' | 'bottom' | 'between'; key: string }[] = [
  { k: 'left', key: 'reveal_left' },
  { k: 'right', key: 'reveal_right' },
  { k: 'top', key: 'reveal_top' },
  { k: 'bottom', key: 'reveal_bottom' },
  { k: 'between', key: 'reveal_between' },
];

const num = (v: Scalar | undefined): number | null => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const inchesOf = (item: ResolvedItem, key: string): number | null => {
  const v = item.inputs.find((i) => i.key === key && !i.hidden)?.value;
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isFinite(n)) return n;
  // a fraction typed as text: 17 7/32
  const m = /^(\d+)?\s*(?:(\d+)\/(\d+))?$/.exec(String(v).trim());
  if (!m) return null;
  return Number(m[1] ?? 0) + (m[2] && m[3] ? Number(m[2]) / Number(m[3]) : 0);
};
const frac = (v: number | null): string => (v === null ? '—' : v === 0 ? '0' : formatLength(String(v)));

/**
 * The Fronts view of one wall (Spencer, 2026-10-22): each box at its own width in the order of its marks left to
 * right, its doors and drawer fronts at their resolved sizes (the child's width and height), and every gap between
 * fronts and to the box edges labelled, read from the reveals the box reads (the catalog's door_width formula reads
 * them on the box, so a held reveal lives on the box). A gap at a joint is two boxes' reveals, approved once. Heights are
 * from the box's bottom face; the screen lifts a base by its toe kick.
 */
export function frontsLayout(res: Resolution, lib: Library, boxes: ResolvedItem[], marks: WalkMark[]): FrontsLayout {
  const xOf = (i: ResolvedItem) => Number(marks.find((m) => m.job_item_id === i.id)?.x_pct ?? 0);
  const ordered = [...boxes].filter((b) => inchesOf(b, 'width') !== null).sort((a, b) => xOf(a) - xOf(b) || natural(a.number, b.number));
  const out: FrontsBox[] = [];
  const gaps: Gap[] = [];
  let x = 0;
  let maxH = 0;
  for (const b of ordered) {
    const width = inchesOf(b, 'width') ?? 0;
    const height = inchesOf(b, 'height') ?? 30;
    const reveals = {} as FrontsBox['reveals'];
    for (const r of REVEALS) {
      const use = b.standards_used.find((s) => s.key === r.key);
      const def = lib.standards.find((d) => d.key === r.key);
      reveals[r.k] = { key: r.key, label: def?.label ?? r.key, value: num(use?.value ?? res.standards[r.key] ?? null), standard: num(use?.job_value ?? res.standards[r.key] ?? null), held: !!use?.overridden };
    }
    // what a box makes that is not a front (an applied end panel, a drawer box, a toe kick) is not drawn as one
    const notFront = (i: ResolvedItem) => /end panel|side panel|drawer box|toe kick|shelf|back panel/i.test(`${i.catalog_item_name ?? ''} ${i.name}`);
    const kids = res.items.filter((i) => i.parent_item_id === b.id && !notFront(i) && inchesOf(i, 'width') !== null && inchesOf(i, 'height') !== null).sort((p, q) => (p.child_index ?? 0) - (q.child_index ?? 0) || natural(p.number, q.number));
    const kindOf = (i: ResolvedItem): Front['kind'] => {
      const name = `${i.catalog_item_name ?? ''} ${i.name}`.toLowerCase();
      return /drawer/.test(name) ? 'drawer' : /door/.test(name) ? 'door' : 'panel';
    };
    const doors = kids.filter((k) => kindOf(k) !== 'drawer');
    const drawers = kids.filter((k) => kindOf(k) === 'drawer');
    const fronts: Front[] = [];
    const bottom = reveals.bottom.value ?? 0;
    const top = reveals.top.value ?? 0;
    const left = reveals.left.value ?? 0;
    const between = reveals.between.value ?? 0;
    // drawer fronts stack from the top; doors sit side by side under them
    let y = height - top;
    for (const d of drawers) {
      const h = inchesOf(d, 'height')!;
      y -= h;
      fronts.push({ item: d, kind: 'drawer', x: left, y, width: inchesOf(d, 'width')!, height: h });
      y -= between;
    }
    let dx = left;
    doors.forEach((d, i) => {
      const w = inchesOf(d, 'width')!;
      const h = inchesOf(d, 'height')!;
      fronts.push({ item: d, kind: kindOf(d), x: dx, y: bottom, width: w, height: h });
      if (i > 0) {
        gaps.push({ key: `${b.id}:between:${i}`, x: x + dx - between, width: between, label: `between ${frac(reveals.between.value)}`, held: reveals.between.held, standard: reveals.between.standard, item_ids: [b.id], keys: [{ item_id: b.id, key: 'std:reveal_between', value: reveals.between.value }], row: 1 });
      }
      dx += w + between;
    });
    if (doors.length > 0) {
      gaps.push({ key: `${b.id}:left`, x, width: left, label: `left ${frac(reveals.left.value)}`, held: reveals.left.held, standard: reveals.left.standard, item_ids: [b.id], keys: [{ item_id: b.id, key: 'std:reveal_left', value: reveals.left.value }], row: 1 });
      const right = reveals.right.value ?? 0;
      gaps.push({ key: `${b.id}:right`, x: x + width - right, width: right, label: `right ${frac(reveals.right.value)}`, held: reveals.right.held, standard: reveals.right.standard, item_ids: [b.id], keys: [{ item_id: b.id, key: 'std:reveal_right', value: reveals.right.value }], row: 1 });
    }
    out.push({ item: b, x, width, height, fronts, reveals });
    x += width;
    if (height > maxH) maxH = height;
  }
  // a joint: the right reveal of one box and the left reveal of the next read as one gap, approved once on both
  for (let i = 1; i < out.length; i++) {
    const a = out[i - 1]!;
    const b = out[i]!;
    if (a.fronts.length === 0 || b.fronts.length === 0) continue;
    const r = a.reveals.right;
    const l = b.reveals.left;
    const value = (r.value ?? 0) + (l.value ?? 0);
    const standard = r.standard === null && l.standard === null ? null : (r.standard ?? 0) + (l.standard ?? 0);
    gaps.push({
      key: `joint:${a.item.id}:${b.item.id}`,
      x: b.x - (r.value ?? 0),
      width: value,
      label: `doors ${frac(value)} apart · ${frac(r.value)} + ${frac(l.value)}`,
      held: r.held || l.held,
      standard,
      item_ids: [a.item.id, b.item.id],
      keys: [{ item_id: a.item.id, key: 'std:reveal_right', value: r.value }, { item_id: b.item.id, key: 'std:reveal_left', value: l.value }],
      row: 0,
    });
  }
  return { boxes: out, gaps, width: x, height: maxH };
}
